import { Button, Card, Header, ListItem } from "semantic-ui-react";
import { NewPageComponent } from "../components/page/NewPageComponent";
import { NEW_LIST_FILTERED_ELEMENTS } from "../routes/queries";
import {
  appendIdToFormFields,
  getEventMetadata,
  getSession,
  useMutationWrapper,
} from "../helper";
import { getProjectFormFields } from "./project_form";
import { NewNewFormComponentProps } from "../components/new_form/NewNewFormComponent";
import {
  CREATE_NEW_ELEMENT,
  CREATE_REFERENCE_LIBRARY_ELEMENT,
  NEW_COMPLETE_ELEMENT,
  NEW_DELETE_ELEMENT,
  NEW_UPDATE_ELEMENT,
} from "../routes/mutations";
import { CardDotMenuComponent } from "../components/new_card/dot_menu/CardDotMenuComponent";
import { NewNewModalWithFormComponent } from "../components/new_modal/NewNewModalWithFormComponent";
import { getNewNextActionFormFields } from "../nextaction/next_action_form";
import { SettingsCache } from "../cache/SettingsCache";
import { getNewWaitingForFormFields } from "../waitingfor/waiting_for_form";
import { LIST_LIFE_DOMAINS } from "../life_domain/routes";
import { ReferenceLibraryElementLink } from "../referencelibrary/ReferenceLibraryElementLink";
import { getReferenceLibraryForm } from "src/referencelibrary/reference_library_form";
import { LinkedReferenceLibraryElementsSection } from "src/referencelibrary/ReferenceLibraryElementSection";

const ElementEntry = (props: {
  data: any;
  deleteCallback: (id: number) => void;
}) => {
  const [completeElementMutation] = useMutationWrapper(
    NEW_COMPLETE_ELEMENT.documentNode,
    (data) => {
      props.deleteCallback(props.data.id);
    },
  );

  return (
    <ListItem
      className="element"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "0.5em",
        marginBottom: "0.5em",
      }}
    >
      <span>{props.data.name}</span>
      <Button
        positive
        onClick={() => {
          completeElementMutation({
            variables: {
              id: props.data.id,
              session: getSession(),
              eventMetadata: getEventMetadata(),
              isChildElement: true,
            },
          });
        }}
      >
        Complete
      </Button>
    </ListItem>
  );
};

const ProjectCard = (props: {
  data: any;
  setReloadKey: any;
  pageData: any;
  setPageData: any;
}) => {
  const [completeProject] = useMutationWrapper(
    NEW_COMPLETE_ELEMENT.documentNode,
    (data) => {
      props.setReloadKey((k: number) => k + 1);
    },
  );

  return (
    <Card fluid key={props.data.id}>
      <Card.Content>
        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          className="card-header"
        >
          <Card.Header style={{ fontSize: 18 }}>
            <b>{props.data.name}</b>
          </Card.Header>
          <CardDotMenuComponent
            setReloadKey={props.setReloadKey}
            pageData={props.pageData}
            setPageData={props.setPageData}
            cardData={props.data}
            props={{
              menuItems: [
                {
                  type: "api_call",
                  label: "Delete",
                  apiCall: NEW_DELETE_ELEMENT,
                  callback: (
                    data: any,
                    setReloadKey: any,
                    pageData: any,
                    setPageData: any,
                  ) => {
                    setReloadKey((k: number) => k + 1);
                  },
                  apiCallVariables: (data: any) => {
                    return {
                      id: props.data.id,
                      session: getSession(),
                      eventMetadata: getEventMetadata(),
                      isChildElement: true,
                    };
                  },
                  showMenuOption: (data: any) => {
                    return data.isChildElement;
                  },
                },
                {
                  type: "new_form_modal",
                  label: "Delete",
                  apiCall: NEW_DELETE_ELEMENT,
                  modalHeader: "Choose Delete Strategy for " + props.data.name,
                  showMenuOption: (data: any) => {
                    return !data.isChildElement && !data.parentElement;
                  },
                  formComponentProps: new NewNewFormComponentProps({
                    form: {
                      formFields: [
                        {
                          type: "radio_button",
                          key: "parentDeletionStrategy",
                          label:
                            "When deleting this element, what should be done with its child elements?",
                          options: [
                            {
                              value: "delete_all",
                              label: "Delete all child elements",
                            },
                            {
                              value: "orphan_children",
                              label: "Keep all child elements",
                            },
                          ],
                        },
                      ],
                      type: "single_form",
                    },
                    startingData: {
                      id: props.data.id,
                      isChildElement: false,
                      parentDeletionStrategy: "delete_all",
                    },
                  }),
                  callback: (data) => {
                    props.setReloadKey((k: number) => k + 1);
                  },
                },
                {
                  type: "new_form_modal",
                  label: "Delete",
                  apiCall: NEW_DELETE_ELEMENT,
                  modalHeader: "Choose Delete Strategy for " + props.data.name,
                  showMenuOption: (data: any) => {
                    return !data.isChildElement && data.parentElement;
                  },
                  formComponentProps: new NewNewFormComponentProps({
                    form: {
                      formFields: [
                        {
                          type: "radio_button",
                          key: "parentDeletionStrategy",
                          label:
                            "When deleting this element, what should be done with its child elements?",
                          options: [
                            {
                              value: "delete_all",
                              label: "Delete all child elements",
                            },
                            {
                              value: "orphan_children",
                              label: "Keep all child elements",
                            },
                            {
                              value: "promote_children",
                              label:
                                "Promote children to be under element's parent element",
                            },
                          ],
                        },
                      ],
                      type: "single_form",
                    },
                    startingData: {
                      id: props.data.id,
                      isChildElement: false,
                      parentDeletionStrategy: "delete_all",
                    },
                  }),
                  callback: (data) => {
                    props.setReloadKey((k: number) => k + 1);
                  },
                },

                {
                  type: "new_form_modal",
                  apiCall: NEW_UPDATE_ELEMENT,
                  label: "Edit",
                  modalHeader: "Edit Someday/Maybe Element " + props.data.name,
                  showMenuOption: () => {
                    return true;
                  },
                  formComponentProps: new NewNewFormComponentProps({
                    form: {
                      formFields: appendIdToFormFields(
                        getProjectFormFields(),
                        props.data,
                      ),
                      type: "single_form",
                    },
                    startingData: {
                      name: props.data.name,
                      lifeDomainId: props.data.lifeDomain
                        ? props.data.lifeDomain.id
                        : undefined,
                      parentElementId: props.data.parentElement
                        ? props.data.parentElement.id
                        : undefined,
                      id: props.data.id,
                    },
                  }),
                  callback: (data) => {
                    props.setReloadKey((k: number) => k + 1);
                  },
                },
              ],
            }}
          />
        </div>
      </Card.Content>
      <Card.Content className="card-details">
        <Header>Details</Header>
        {props.data.lifeDomain && (
          <div className="life-domain-details">
            <b>Life Domain: </b>
            {props.data.lifeDomain.name}
          </div>
        )}
      </Card.Content>
      <Card.Content>
        <Header>Subprojects</Header>
        {(props.data.children || []).map((child: any) => (
          <ProjectCard
            key={child.id}
            data={child}
            setReloadKey={props.setReloadKey}
            pageData={props.pageData}
            setPageData={props.setPageData}
          />
        ))}
      </Card.Content>
      <Card.Content className="next-actions">
        <Header>Next Actions</Header>
        {props.data.childNextActions.map((child: any) => (
          <ElementEntry
            key={child.id}
            data={child}
            deleteCallback={() => {
              props.setReloadKey((k: number) => k + 1);
            }}
          />
        ))}
        <NewNewModalWithFormComponent
          props={{
            header: "Create Next Action",
            triggerProps: {
              label: "Create Next Action",
              type: "button",
            },
            formComponentProps: new NewNewFormComponentProps({
              form: {
                formFields: getNewNextActionFormFields(),
                type: "single_form",
              },
              startingData: {
                name: "",
                parentElementId: props.data.id,
                priority: SettingsCache.getInstance().getDefaultPriority(),
              },
            }),
            apiCall: CREATE_NEW_ELEMENT,
            callback: () => props.setReloadKey((prev: number) => prev + 1),
          }}
        />
      </Card.Content>
      <Card.Content className="waiting-fors">
        <Header>Waiting Fors</Header>
        {props.data.childWaitingFors.map((child: any) => (
          <ElementEntry
            key={child.id}
            data={child}
            deleteCallback={() => {
              props.setReloadKey((k: number) => k + 1);
            }}
          />
        ))}
        <NewNewModalWithFormComponent
          props={{
            header: "Create Waiting For",
            triggerProps: {
              label: "Create Waiting For",
              type: "button",
            },
            formComponentProps: new NewNewFormComponentProps({
              form: {
                formFields: getNewWaitingForFormFields(),
                type: "single_form",
              },
              startingData: {
                name: "",
                parentElementId: props.data.id,
                priority: SettingsCache.getInstance().getDefaultPriority(),
              },
            }),
            apiCall: CREATE_NEW_ELEMENT,
            callback: () => props.setReloadKey((prev: number) => prev + 1),
          }}
        />
      </Card.Content>
      <Card.Content className="create-sub-project">
        <NewNewModalWithFormComponent
          props={{
            header: "Create Project",
            triggerProps: {
              label: "Create Project",
              type: "button",
            },
            formComponentProps: new NewNewFormComponentProps({
              form: {
                formFields: getProjectFormFields(),
                type: "single_form",
              },
              startingData: {
                name: "",
                lifeDomainId: undefined,
                parentElementId: props.data.id,
              },
            }),
            apiCall: CREATE_NEW_ELEMENT,
            callback: () => props.setReloadKey((prev: number) => prev + 1),
          }}
        />
      </Card.Content>
      <LinkedReferenceLibraryElementsSection
        data={props.data}
        setReloadKey={props.setReloadKey}
      />
      <Card.Content className="complete-project">
        {props.data.isChildElement && (
          <Button
            positive
            onClick={() => {
              completeProject({
                variables: {
                  session: getSession(),
                  id: props.data.id,
                  eventMetadata: getEventMetadata(),
                  isChildElement: true,
                },
              });
            }}
          >
            Complete Project
          </Button>
        )}
        {props.data.parentElement && !props.data.isChildElement && (
          <NewNewModalWithFormComponent
            props={{
              header: "Complete Project",
              triggerProps: {
                label: "Complete Project",
                type: "button",
              },
              formComponentProps: new NewNewFormComponentProps({
                form: {
                  formFields: [
                    {
                      type: "radio_button",
                      key: "parentCompletionStrategy",
                      label:
                        "When completing this element, what should be done with its child elements?",
                      options: [
                        {
                          value: "complete_all_children",
                          label: "Complete all child elements",
                        },
                        {
                          value: "orphan_children",
                          label: "Keep all child elements",
                        },
                        {
                          value: "promote_children",
                          label:
                            "Promote children to be under element's parent element",
                        },
                      ],
                    },
                    {
                      type: "hardcoded",
                      key: "id",
                      value: props.data.id,
                    },
                    {
                      type: "hardcoded",
                      key: "isChildElement",
                      value: false,
                    },
                    {
                      type: "hardcoded",
                      key: "eventMetadata",
                      value: getEventMetadata(),
                    },
                  ],
                  type: "single_form",
                },
                startingData: {
                  parentCompletionStrategy: "complete_all_children",
                },
              }),
              apiCall: NEW_COMPLETE_ELEMENT,
              callback: () => props.setReloadKey((prev: number) => prev + 1),
            }}
          />
        )}
        {!props.data.parentElement && !props.data.isChildElement && (
          <NewNewModalWithFormComponent
            props={{
              header: "Complete Project",
              triggerProps: {
                label: "Complete Project",
                type: "button",
              },
              formComponentProps: new NewNewFormComponentProps({
                form: {
                  formFields: [
                    {
                      type: "radio_button",
                      key: "parentCompletionStrategy",
                      label:
                        "When completing this element, what should be done with its child elements?",
                      options: [
                        {
                          value: "complete_all_children",
                          label: "Complete all child elements",
                        },
                        {
                          value: "orphan_children",
                          label: "Keep all child elements",
                        },
                      ],
                    },
                    {
                      type: "hardcoded",
                      key: "id",
                      value: props.data.id,
                    },
                    {
                      type: "hardcoded",
                      key: "isChildElement",
                      value: false,
                    },
                    {
                      type: "hardcoded",
                      key: "eventMetadata",
                      value: getEventMetadata(),
                    },
                  ],
                  type: "single_form",
                },
                startingData: {
                  parentCompletionStrategy: "complete_all_children",
                },
              }),
              apiCall: NEW_COMPLETE_ELEMENT,
              callback: () => props.setReloadKey((prev: number) => prev + 1),
            }}
          />
        )}
      </Card.Content>
    </Card>
  );
};

export const NewProjectsPage = () => {
  return (
    <NewPageComponent
      props={{
        type: "parent_child_list",
        card: ProjectCard,
        parentItem: "parentElement",
        query: NEW_LIST_FILTERED_ELEMENTS,
        queryVariables: {
          session: getSession(),
          type: 4,
        },
        itemName: "Project",
        creationProps: {
          formComponentProps: new NewNewFormComponentProps({
            form: {
              formFields: getProjectFormFields(),
              type: "single_form",
            },
            startingData: {
              name: "",
              lifeDomainId: undefined,
              parentElementId: undefined,
            },
          }),
          apiCall: CREATE_NEW_ELEMENT,
        },
        filterComponentProps: {
          form: {
            formFields: [
              {
                type: "query_dropdown",
                key: "lifeDomainId",
                label: "Life Domain",
                required: false,
                noSelectionLabel: "No Life Domain",
                apiCall: {
                  documentNode: LIST_LIFE_DOMAINS,
                  key: "listLifeDomains",
                },
                apiVariables: {
                  session: getSession(),
                },
              },
            ],
            type: "single_form",
          },
          startingData: {},
        },
      }}
    />
  );
};
